import { Box, Typography } from '@mui/material';
import React, { useContext, useState } from 'react';
import { i18n } from '../../i18n/i18n';
import MuiDialogWrapper from '../../components/MuiDialogWrapper/MuiDialogWrapper';
import {
    IconConnections,
    IconInsights,
    IconLocations,
    IconMedia,
    IconPersonalInfo,
    IconPersonOverview,
    IconPosts,
    IconSocialPresence,
    IconWebMentions,
} from '../../images';
import CustomCheckbox from '../../components/CustomCheckbox/CustomCheckbox';
import _ from 'lodash';
import { GlobalContext } from '../../contexts/globalContext';
import { getDateRangeBody } from '../../utils/communication';
import { LOADER } from '../../constants/constants';

interface ExportPdfDialogProps {
    close: () => void;
    data: any;
}

const ExportPdfDialog: React.FC<ExportPdfDialogProps> = ({ close, data }) => {
    const { reportData } = useContext(GlobalContext);
    const isExist = (name: string) => {
        let exist: boolean;
        switch (name) {
            case 'profileOverview':
                exist = reportData.status.summary === LOADER.STATUS.LOADED;
                break;
            case 'personalInformation':
                exist = reportData.status.personalInfo === LOADER.STATUS.LOADED;
                break;
            case 'insights':
                exist = reportData.status.insights === LOADER.STATUS.LOADED;
                break;
            case 'socialPresence':
                exist = reportData.status.socialPresence === LOADER.STATUS.LOADED;
                break;
            case 'connections':
                exist = reportData.status.connections === LOADER.STATUS.LOADED;
                break;
            case 'media':
                exist = reportData.status.media === LOADER.STATUS.LOADED;
                break;
            case 'locations':
                exist = reportData.status.locations === LOADER.STATUS.LOADED;
                break;
            case 'posts':
                exist = reportData.status.posts === LOADER.STATUS.LOADED;
                break;
            case 'webMentions':
                exist = reportData.status.webMentions === LOADER.STATUS.LOADED;
                break;
        }
        return Boolean(exist);
    };
    const [exportOptions, setExportOptions] = useState({
        profileOverview: isExist('profileOverview'),
        socialPresence: isExist('socialPresence'),
        media: isExist('media'),
        personalInformation: isExist('personalInformation'),
        connections: isExist('connections'),
        locations: isExist('locations'),
        insights: isExist('insights'),
        posts: isExist('posts'),
        webMentions: isExist('webMentions'),
        includeFilteredData: true,
        extractImagesToZip: false,
        filters: reportData.exportFilters,
        tags: reportData.tags ? reportData.tags.split(',') : [],
        dates: getDateRangeBody(reportData.dateRange).dates,
        reportType: 'PDF',
    });
    const onActionHandler = () => {
        data.onCreate(exportOptions);
        close();
    };
    const views = [
        {
            name: i18n.translate('Summary'),
            icon: <IconPersonOverview className="fill-md" />,
            key: 'profileOverview',
        },
        {
            name: i18n.translate('Social Presence'),
            icon: <IconSocialPresence className="fill-md" />,
            key: 'socialPresence',
        },
        {
            name: i18n.translate('Media'),
            icon: <IconMedia className="fill-md" />,
            key: 'media',
        },
        {
            name: i18n.translate('Personal Information'),
            icon: <IconPersonalInfo className="fill-md" />,
            key: 'personalInformation',
        },
        {
            name: i18n.translate('Connections'),
            icon: <IconConnections className="fill-md" />,
            key: 'connections',
        },
        {
            name: i18n.translate('Locations'),
            icon: <IconLocations className="fill-md" />,
            key: 'locations',
        },
        {
            name: i18n.translate('Posts'),
            icon: <IconPosts className="fill-md" />,
            key: 'posts',
        },
        {
            name: i18n.translate('Web Mentions'),
            icon: <IconWebMentions className="fill-md" />,
            key: 'webMentions',
        },
    ];
    const updateOptions = (view, exportOptions) => {
        exportOptions[view.key] = !exportOptions[view.key];
    };
    return (
        <MuiDialogWrapper
            styles={{ ...data.styles, ...{ '& .MuiDialogActions-root': { p: '14px 24px !important' } } }}
            title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="s18w700">{i18n.translate('Create a PDF report')}</Typography>
                </Box>
            }
            onClose={close}
            content={
                <Box>
                    <Typography variant="s14w700" className="color-bd">
                        {i18n.translate('Choose the views for your report')}
                    </Typography>
                    <Box
                        sx={{
                            backgroundColor: '#D0D3D71F',
                            width: 638,
                            mt: 2,
                            p: 2,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 2 }}>
                            {views.map((view, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        width: '33%',
                                        height: 32,
                                        opacity: isExist(view.key) ? null : 0.5,
                                    }}
                                >
                                    <CustomCheckbox
                                        disabled={!isExist(view.key)}
                                        checked={exportOptions[view.key]}
                                        onClick={() => {
                                            updateOptions(view, exportOptions);
                                            setExportOptions(_.cloneDeep(exportOptions));
                                        }}
                                    />
                                    <Box
                                        sx={{ cursor: 'pointer', display: 'flex' }}
                                        onClick={() => {
                                            if (isExist(view.key)) {
                                                updateOptions(view, exportOptions);
                                                setExportOptions(_.cloneDeep(exportOptions));
                                            }
                                        }}
                                    >
                                        {view.icon}
                                    </Box>
                                    <Typography
                                        className="color-bd"
                                        sx={{ pl: view.icon ? 1 : 0, cursor: 'pointer' }}
                                        onClick={() => {
                                            if (isExist(view.key)) {
                                                updateOptions(view, exportOptions);
                                                setExportOptions(_.cloneDeep(exportOptions));
                                            }
                                        }}
                                    >
                                        {view.name}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="s14w700" className="color-bd">
                            {i18n.translate('Add more settings')}
                        </Typography>
                        <Box
                            sx={{ display: 'inline-flex', alignItems: 'center', height: 32, cursor: 'pointer' }}
                            onClick={() => {
                                exportOptions.extractImagesToZip = !exportOptions.extractImagesToZip;
                                setExportOptions(_.cloneDeep(exportOptions));
                            }}
                        >
                            <CustomCheckbox checked={exportOptions.extractImagesToZip} />
                            <Typography className="color-bd">
                                {i18n.translate('Extract images to a zip file')}
                            </Typography>
                            <Typography className="color-md" sx={{ ml: 0.5 }}>
                                {`(${i18n.translate('this action may take some time')})`}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', height: 32 }}>
                            <CustomCheckbox
                                checked={exportOptions.includeFilteredData}
                                onClick={() => {
                                    exportOptions.includeFilteredData = !exportOptions.includeFilteredData;
                                    if (exportOptions.includeFilteredData) {
                                        exportOptions.tags = reportData.tags ? reportData.tags.split(',') : [];
                                        exportOptions.filters = reportData.exportFilters;
                                    } else {
                                        exportOptions.tags = [];
                                        exportOptions.filters = reportData.getInitialExportFilters();
                                    }
                                    setExportOptions(_.cloneDeep(exportOptions));
                                }}
                            />
                            <Typography className="color-bd">
                                {i18n.translate('Include filtered results only')}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            }
            buttons={[
                {
                    name: i18n.translate('Create'),
                    onClick: onActionHandler,
                    styles: { width: '74px !important', height: 32 },
                },
            ]}
        />
    );
};

export default ExportPdfDialog;
